import 'helpers/broadcast-channel/polyfill';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Tracker from '@openreplay/tracker';
import { UserAccount } from 'api/account/account.types';
import {
  CreateOrUpdateMemberResponse,
  createOrUpdatePatient,
} from 'api/account/createOrUpdatePatient';
import { getCurrentAccount } from 'api/account/getCurrentAccount';
import { logoutAccount } from 'api/account/logoutAccount';
import { getAccountPatients } from 'api/account/patients';
import { Patient } from 'api/account/patients.type';
import { updateAccount, UpdateAccountVars } from 'api/account/updateAccount';
import { getAccountLeads } from 'api/leads/leads';
import { Lead, Leads } from 'api/leads/leads.type';
import { updateLead as apiUpdateLead } from 'api/leads/updateLead';
import { getConfig } from 'helpers/appConfig';
import { attachAuthHeader } from 'helpers/axiosInstance';
import { Heartbeat } from 'helpers/heartbeat/Heartbeat';
import { FCWithChildren } from 'helpers/types/FCWithChildren';
import { useAuthentication } from 'hooks/useAuthentication';
import { LaunchDarklyService } from 'hooks/useFlag';
import { useMergeState } from 'hooks/useMergeState';
import { usePrevious } from 'hooks/usePrevious';
import { useLocation, useNavigate } from 'react-router-dom';
import { Instrumentation } from 'utils/Instrumentation';

export interface CurrentUserState {
  isLoading: boolean;
  isInitialized: boolean;
  token?: string | null;
  account?: UserAccount | null;
  members?: Patient[] | null;
  leads?: Leads | null;
}

export interface CurrentUserContext {
  logout: () => void;
  setToken: (token: string) => Promise<void>;
  updateAccount: (args: UpdateAccountVars) => Promise<UserAccount>;
  refresh: () => Promise<UserAccount>;
  refreshMembers: () => Promise<Patient[]>;
  createOrUpdateMember: (
    id: string | undefined,
    member: Partial<Patient>,
  ) => Promise<CreateOrUpdateMemberResponse>;
  setAccount: (account: UserAccount) => void;
  state: CurrentUserState;
  isSelf?: boolean;
  isPendingTimeout: boolean;
  refreshLeads: () => Promise<Leads>;
  updateLead: (leadId: string, updates: Partial<Lead>) => Promise<Lead>;
}

const CurrentUserContext = createContext<CurrentUserContext>({
  state: { isInitialized: false, isLoading: false },
  logout: () => undefined,
  setToken: () => Promise.reject(),
  updateAccount: () => Promise.reject(),
  refresh: () => Promise.reject(),
  refreshMembers: () => Promise.reject(),
  isPendingTimeout: false,
  setAccount: () => undefined,
  createOrUpdateMember: () => Promise.reject(),
  refreshLeads: () => Promise.reject(),
  updateLead: () => Promise.reject(),
});

export interface CurrentUserProviderProps {
  requiresSession?: boolean;
}

export const CurrentUserProvider: FCWithChildren<CurrentUserProviderProps> = ({
  children,
  requiresSession,
}) => {
  const [state, setState] = useMergeState<CurrentUserState>({
    isLoading: false,
    isInitialized: false,
  });
  const [isPendingTimeout, setIsPendingTimeout] = useState(false);
  const {
    isAuthenticated,
    isLoading,
    logout: logoutAuthProvider,
    initializeAccessTokenFromStorage,
    user,
    error: authError,
  } = useAuthentication();

  // keycloak errors are Error objects, and may be created anew
  // since we really only care about the error type, we can pull
  // the message into a string, which is safer to use in useEffect hooks
  const authErrorMessage = authError?.toString();
  const hasAuthErrored = !!authErrorMessage;

  const { ssouser } = user || {};

  const prevAuth0Authenticated = usePrevious(isAuthenticated);

  const { pathname } = useLocation();

  const navigate = useNavigate();

  const heartbeat = useRef(new Heartbeat());

  const loadAccountMembersAndLeads = async () => {
    const account = await getCurrentAccount();
    return [
      account,
      await getAccountPatients(),
      await getAccountLeads(),
    ] as const;
  };

  const setAccount = useCallback(
    async (account: UserAccount) => {
      const members = await getAccountPatients();
      setState({ account, members });
    },
    [setState],
  );

  useEffect(() => {
    if (!state?.account) return;

    const config = getConfig();

    if (config.openReplayConfig.enabled) {
      Instrumentation.openReplay = new Tracker({
        projectKey: config.openReplayConfig.projectKey,
        ingestPoint: config.openReplayConfig.ingestPoint,
        __DISABLE_SECURE_MODE: true,
      });

      Instrumentation.openReplay?.setUserID(state.account?.id);
      Instrumentation.openReplay?.start().catch(e => console.error(e));
      return () => {
        Instrumentation.openReplay?.stop();
      };
    }
  }, [state?.account]);

  const setToken = useCallback(
    async (token: string) => {
      attachAuthHeader(token);

      const [account, members, leads] = await loadAccountMembersAndLeads();

      setState({
        isInitialized: true,
        token,
        account,
        members,
        leads,
      });
    },
    [setState],
  );

  const update = async (args: UpdateAccountVars) => {
    const newAccount = await updateAccount(args);

    setState({
      account: newAccount,
    });

    return newAccount;
  };

  const refresh = async () => {
    const [account, members, leads] = await loadAccountMembersAndLeads();

    setState({ account, members, leads });

    return account;
  };

  const refreshMembers = async () => {
    const members = await getAccountPatients();
    setState({ members });

    return members;
  };

  const refreshLeads = async () => {
    const leads = await getAccountLeads();
    setState({ leads });

    return leads;
  };

  const updateLead = async (leadId: string, updates: Partial<Lead>) => {
    const lead = await apiUpdateLead(leadId, updates);

    const idx = state.leads?.findIndex(l => l.id === leadId);

    if (!idx || idx === -1) return lead;

    const leads = [...(state.leads ?? [])];
    leads.splice(idx, 1, lead);

    setState({ leads });

    return lead;
  };

  const createOrUpdateMember = async (
    id: string | undefined,
    params: Partial<Patient>,
  ) => {
    const member = await createOrUpdatePatient(id, params);
    await refreshMembers();
    return member;
  };

  const logout = useCallback(async () => {
    await Promise.all([
      await logoutAccount(),
      await logoutAuthProvider({ returnTo: window.location.origin }),
    ]).catch(e => {
      console.error(e);
    });

    setIsPendingTimeout(false);
    attachAuthHeader(undefined);

    setState({
      account: null,
      members: null,
      isInitialized: false,
      isLoading: false,
      token: null,
    });
    heartbeat.current.clearStorage();
  }, [logoutAuthProvider, setState]);

  // spin up the user session once the auth provider has identified the user
  useEffect(() => {
    (async () => {
      if (
        hasAuthErrored ||
        !requiresSession ||
        !isAuthenticated ||
        prevAuth0Authenticated
      ) {
        return;
      }

      try {
        const token = await initializeAccessTokenFromStorage({
          ignoreCache: false,
        });

        await setToken(token!);
        const [account, members, leads] = await loadAccountMembersAndLeads();

        LaunchDarklyService.initialize(account.id);

        try {
          heartbeat.current.clearStorage();

          const nextState = {
            isLoading: false,
            account,
            members,
            leads,
            isInitialized: true,
            token,
          };
          setState(nextState);

          if (typeof freshpaint !== 'undefined') {
            freshpaint?.identify(freshpaint.cookie?.props?.distinct_id, {
              email: user?.email,
              member_app_id: account.id,
            });
          }
        } catch (e) {
          console.error(e);
        }
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const error = e as unknown as any;
        if (!ssouser) {
          window.localStorage.clear();
          await logoutAuthProvider({ returnTo: window.location.origin });

          setState({ token: null });
        }

        if (
          ssouser &&
          error?.response?.status == 404 &&
          error?.response?.data?.error == 'SSO user does not have an account'
        ) {
          // If the account is not found, we need to render form to create an account
          navigate('/account/create?sso=lww');
        } else {
          console.error(e);
        }
      }
    })();
  }, [
    hasAuthErrored,
    initializeAccessTokenFromStorage,
    isAuthenticated,
    logout,
    logoutAuthProvider,
    navigate,
    pathname,
    prevAuth0Authenticated,
    requiresSession,
    setState,
    setToken,
    ssouser,
    user?.email,
  ]);

  useEffect(() => {
    if (!hasAuthErrored) return;

    // most common error from keycloak is the URL containing a `state` parameter
    // which will cause keycloak to shut down entirely if it doesn't have a matching
    // oidc.{state param value} as a key in localstorage

    // Since it is possible to get one of these by hitting the back button and letting
    // keycloak redirect you back to the member app, the only real solution is to clear out the query string
    // and reload the window

    const qs = new URLSearchParams(window.location.search);
    if (qs.get('state')) {
      window.history.replaceState({}, '', window.location.pathname);
      // keycloak errors are not recoverable, just reload the page
      window.location.reload();
    } else {
      console.error('unrecoverable error', authErrorMessage);
      if (authErrorMessage === 'login_required') return;

      // send a spurious API request that can be found in the nginx logs to help debug
      // this unknown failure case
      fetch(
        `${getConfig().baseApiPath}/log?${new URLSearchParams({
          // keycloak properties
          error: authErrorMessage,
          token: state.token ?? '',
          kcUser: user ? JSON.stringify(user) : '',
          kcIsLoading: `${isLoading}`,
          kcIsAuthenticated: `${isAuthenticated}`,
          // user and device properties
          accountId: state.account?.id ?? '',
          screenRatio: `${window.screen.width}x${window.window.screen.height}`,
          qs: qs.toString() ?? '',
        })}`,
      );

      window.localStorage.clear();
      window.location.href = '/session-error';
    }
    // only trigger the effect if the error changes
    // the error is an object, which may be unique every time this mounts
    // we really only want to re-run the effect if the error message changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authErrorMessage]);

  const isSelfInCare = useMemo(() => {
    return (
      !!state.members?.find(m => m.personalAccountId === state.account?.id) ??
      false
    );
  }, [state.account?.id, state.members]);

  useEffect(() => {
    if (state.account) {
      setIsPendingTimeout(false);
      heartbeat.current.spinup();
    } else {
      heartbeat.current.shutdown();
    }
  }, [state.account]);

  heartbeat.current.onTimeout = logout;
  heartbeat.current.onWarn = () => setIsPendingTimeout(true);
  heartbeat.current.onPersist = () => setIsPendingTimeout(false);

  const context: CurrentUserContext = {
    state,
    logout,
    setToken,
    updateAccount: update,
    refresh,
    refreshMembers,
    refreshLeads,
    createOrUpdateMember,
    isPendingTimeout,
    setAccount,
    isSelf: isSelfInCare,
    updateLead,
  };

  // If we don't have an account, but we're authenticated, we need to wait till account is loaded
  if (
    isLoading ||
    (!state.account &&
      isAuthenticated &&
      pathname !== '/account/create' &&
      pathname !== '/login')
  ) {
    return (
      <CurrentUserContext.Provider value={context}>
        Loading...
      </CurrentUserContext.Provider>
    );
  }

  return (
    <CurrentUserContext.Provider value={context}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export const useCurrentUser = () => {
  const context = useContext<CurrentUserContext>(CurrentUserContext);

  if (!context)
    throw new Error(
      '!--- useCurrentUser must be used inside a CurrentUserProvider ---!',
    );

  return context;
};
